import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { midpointMm, PointMm } from '~/domain/geometry/patientGeometry';
import { LineProfile } from '~/domain/intensity/lineProfile';
import {
    ProfileComparison,
    buildProfileComparison,
} from '~/domain/intensity/lineProfileDatasets';
import {
    AnnotationListController,
    useAnnotationListController,
} from '~/hooks/useAnnotationListController';
import { useImageForSeries } from '~/hooks/image/useImageForSeries';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { dicomProcessorVersionState } from '~/state/atoms/dicomProcessorAtoms';
import {
    highlightedLineProfileState,
    lineProfileListState,
} from '~/state/atoms/intensityToolAtoms';

export interface LineProfileController
    extends AnnotationListController<LineProfile> {
    /**
     * The profile sampled across every loaded co-registered dataset, grouped by
     * display unit for overlay comparison, with what the comparison omits.
     */
    comparisonForProfile: (profile: LineProfile) => ProfileComparison;
}

/** Where go-to moves the crosshair: the midpoint, which is on the profile's slice. */
const midpointOf = (profile: LineProfile): PointMm =>
    midpointMm(profile.startMm, profile.endMm);

/**
 * The list actions for line profiles (show/hide, delete, clear all,
 * highlight, go-to) and the comparison of a profile across the loaded
 * datasets. Placing a profile and sampling it is the engine tool's job, not
 * this hook's.
 */
export function useLineProfileController(): LineProfileController {
    const list = useAnnotationListController(
        lineProfileListState,
        highlightedLineProfileState,
        midpointOf
    );
    const imageForSeries = useImageForSeries();
    const patient = useSelectedPatient();
    // patient.images/doses are mutated in place on import/delete, so subscribe
    // to the processor-version counter (bumped by both paths) to refresh the
    // comparison. Deliberately NOT useDomainVersion(): it fires on every
    // changeset (brush stroke, rename) and each refresh re-samples every
    // co-registered dataset along the line.
    const dicomProcessorVersion = useRecoilValue(dicomProcessorVersionState);

    const comparisonForProfile = useCallback(
        (profile: LineProfile): ProfileComparison =>
            buildProfileComparison(
                profile,
                patient?.images ?? [],
                patient?.doses ?? [],
                imageForSeries(profile.plane.seriesInstanceUid)
            ),
        // dicomProcessorVersion is a refresh tripwire, not read in the body.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [patient, imageForSeries, dicomProcessorVersion]
    );

    return { ...list, comparisonForProfile };
}
